import * as React from "react";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { Field, useFormikContext } from "formik";
import styled from "styled-components";
import isFunction from "lodash/isFunction";
import { Input, Checkbox } from "components/bootstrap";
import Select from "components/common/Select";
import usePluginEntities from "hooks/usePluginEntities";
import { defaultCompare } from "logic/DefaultCompare";
import { TIMESTAMP_FIELD } from "views/Constants";
import { DateType } from "views/logic/aggregationbuilder/Pivot";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import VisualizationConfigurationOptions from "./VisualizationConfigurationOptions";
import ElementConfigurationContainer from "../ElementConfigurationContainer";
const EventAnnotationCheckbox = styled(Checkbox)`
  input[type='checkbox'] {
    margin-right: 0;
    right: 0;
  }
`;
const isTimeline = (values) => {
  var _a, _b;
  if (!((_a = values.groupBy) == null ? void 0 : _a.groupings) || values.groupBy.groupings.length === 0) {
    return false;
  }
  const firstRowGrouping = values.groupBy.groupings.find((grouping) => grouping.direction === "row");
  return (firstRowGrouping == null ? void 0 : firstRowGrouping.type) === DateType && ((_b = firstRowGrouping == null ? void 0 : firstRowGrouping.fields) == null ? void 0 : _b[0]) === TIMESTAMP_FIELD;
};
const VisualizationConfiguration = () => {
  var _a, _b, _c;
  const { t } = useTranslation("aggregation");
  const visualizationTypes = usePluginEntities("visualizationTypes");
  const findVisualizationType = useCallback(
    (type) => visualizationTypes.find((visualizationType) => visualizationType.type === type),
    [visualizationTypes]
  );
  const visualizationTypeOptions = useMemo(
    () => visualizationTypes.sort((v1, v2) => defaultCompare(v1.displayName, v2.displayName)).map(({ displayName, type }) => ({ label: displayName, value: type })),
    [visualizationTypes]
  );
  const { values, setFieldValue } = useFormikContext();
  const currentVisualizationType = findVisualizationType(values.visualization.type);
  const sendTelemetry = useSendTelemetry();
  const setNewVisualizationType = useCallback(
    (newValue) => {
      var _a2, _b2;
      const type = findVisualizationType(newValue);
      const createConfig = (_b2 = (_a2 = type.config) == null ? void 0 : _a2.createConfig) != null ? _b2 : () => ({});
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.VISUALIZATION_TYPE_SELECTED, {
        app_section: "search-widget",
        app_action_value: "visualization-type-select",
        event_details: { visualizationType: newValue }
      });
      setFieldValue(
        "visualization",
        {
          type: newValue,
          config: createConfig()
        },
        true
      );
    },
    [findVisualizationType, sendTelemetry, setFieldValue]
  );
  const isTimelineChart = isTimeline(values);
  const supportsEventAnnotations = (_b = (_a = currentVisualizationType.capabilities) == null ? void 0 : _a.includes("event-annotations")) != null ? _b : false;
  const configurationOptionFields = useMemo(() => {
    var _a2;
    const fields = (_a2 = currentVisualizationType.config) == null ? void 0 : _a2.fields;
    if (!isFunction(fields)) return fields != null ? fields : [];
    return fields({ formValues: values });
  }, [(_c = currentVisualizationType.config) == null ? void 0 : _c.fields, values]);
  return /* @__PURE__ */ React.createElement(ElementConfigurationContainer, { elementTitle: t("wizard_common.visualization") }, /* @__PURE__ */ React.createElement(Field, { name: "visualization.type" }, ({ field: { name, value }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "visualization-type-select",
      label: t("wizard_visualization.type"),
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        options: visualizationTypeOptions,
        "aria-label": t("wizard_visualization.select_type"),
        clearable: false,
        name,
        value,
        onChange: (newValue) => {
          if (newValue !== value) {
            setNewVisualizationType(newValue);
          }
        },
        size: "small"
      }
    )
  )), isTimelineChart && supportsEventAnnotations && /* @__PURE__ */ React.createElement(Field, { name: "visualization.eventAnnotation" }, ({ field: { name, value = false }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: `${name}-input`,
      label: t("wizard_visualization.show_event_annotations"),
      error,
      labelClassName: "col-sm-11",
      wrapperClassName: "col-sm-1"
    },
    /* @__PURE__ */ React.createElement(
      EventAnnotationCheckbox,
      {
        id: `${name}-input`,
        name,
        onChange: () => setFieldValue(name, !value),
        checked: value,
        className: "pull-right"
      }
    )
  )), /* @__PURE__ */ React.createElement(VisualizationConfigurationOptions, { name: "visualization.config", fields: configurationOptionFields }));
};
export default VisualizationConfiguration;
